/**
 * How a ship handles.
 *
 * The numbers a flight loop needs, kept out of the renderer so they can be
 * reasoned about and tested without a camera. None of this integrates anything
 * — it answers "how fast, how hard, how quickly" and lets the view do the
 * stepping.
 *
 * Everything derives from thrust, which is already derived from what a ship
 * draws. So a hull that costs more power moves better, with no separate table
 * of handling stats to keep in step with the parts list.
 */
/**
 * How fast anything flies, in world units per second.
 *
 * A flat ceiling, deliberately. Thrust decides how quickly you *reach* it, not
 * how fast you end up — so a heavily-driven hull is one that gets up to speed
 * and stops again in a fraction of the distance, rather than one that simply
 * travels faster. That is the difference that matters when the thing you are
 * doing is arriving somewhere precisely.
 *
 * Deliberately *local*. Thrusters are for working around a station, a planet or
 * an anomaly — not for crossing a system, which is the warp drive's job. A
 * thruster speed high enough to cover the gap between planets in a reasonable
 * time is exactly what made the world feel small: if you can fly anywhere, the
 * distances stop meaning anything.
 *
 * Against the scale that leaves: a station is a few hundred units across and a
 * planet about 2,900, so this crosses a station in seconds and takes half a
 * minute to run the length of a world. Adjacent planetary orbits are ~17,000
 * apart, which at this speed is well over two minutes — unreasonable on
 * purpose. That gap is what the warp drive is for.
 */
export const CRUISE_SPEED = 120;
/**
 * World units per second squared, per unit of thrust.
 *
 * Low, on purpose. A ship that reaches cruise in half a second has no mass —
 * it reads as a cursor being dragged rather than a vessel being flown, and
 * every arrival becomes a matter of stopping instantly rather than of judging
 * a distance. A typical hull now takes several seconds to wind up and needs
 * room to shed it again.
 */
const ACCELERATION_PER_THRUST = 1.6;
/**
 * Radians per second at one unit of thrust, before scaling.
 *
 * Turn rate goes with the *square root* of thrust, not with thrust itself. A
 * ship with eight times the thrust is also a much bigger ship, so scaling
 * linearly had a well-driven hull spinning at four hundred degrees a second —
 * unflyable, and unrecoverable once you had started. The square root keeps a
 * strong hull noticeably more responsive without making it a top.
 */
const TURN_PER_THRUST = 0.16;
/**
 * How roll compares to pitch and yaw.
 *
 * Slower, deliberately. Roll is used to orient before a manoeuvre rather than
 * during one, and a fast roll is disorienting rather than expressive.
 */
const ROLL_FRACTION = 0.55;
/**
 * How much of a hull's thrust is available sideways.
 *
 * Strafing is for lining up on a pad, not for travelling, so it is deliberately
 * a fraction of what the same thrusters do forward.
 */
const STRAFE_FRACTION = 0.45;
/**
 * How much better the precision set strafes.
 *
 * Sideways drift bleeds off at a fixed rate, so a strafe settles at roughly
 * `strafeAcceleration / drag` — which makes this a *speed* multiplier, not just
 * a snappier response. Doubled because translating without turning is the whole
 * job of the manoeuvring set: lining a hull up over a plate is done with strafe
 * and lift, and on the old figure the main drive was the faster way to move
 * sideways, which is exactly backwards.
 */
const PRECISION_STRAFE_BONUS = 2;
/**
 * Speed past which strafing stops being useful, as a fraction of top speed.
 *
 * Below this, sideways authority is full; above it, it falls away to nothing.
 * The effect is that strafe is a docking control — precise when you are
 * crawling, irrelevant when you are crossing a system — without ever being
 * switched off by a rule the player has to learn.
 */
const STRAFE_FALLOFF_START = 0.25;
/**
 * The speed at which turning has noticeably begun to cost something.
 *
 * Set above the precision set's ceiling, so manoeuvring around a pad — the one
 * job where you are turning constantly and cannot afford to be slow at it — sits
 * in the flat part of the curve.
 */
const TURN_REFERENCE = 25;
/** How hard the curve bites. Higher is a stiffer ship at speed. */
const TURN_FALLOFF = 1.6;
/**
 * How far a body's grip reaches, as a multiple of its own radius.
 *
 * A hair past the surface, and no further. The first cut reached nine radii and
 * was far too much: a planet grips for tens of thousands of units that way, so
 * you spend most of a transit locked out of warp by something you are nowhere
 * near. A lock should mean *arrived*, not *approaching*.
 *
 * Planets get the wider band only because they are the thing you are most often
 * trying to hold position against, and because at their size the extra tenth is
 * still a long way in absolute terms.
 */
export const MASS_LOCK_REACH = {
    planet: 1.56,
    moon: 1.44,
    /**
     * None. An asteroid field does not hold anything.
     *
     * Mass lock is mass, and a field has none worth the name — it is a great many
     * small rocks, none of them near a moon. Locking on one would make every belt
     * a wall across the system and every crossing a stop, when the point of a
     * field is that you fly into it.
     *
     * Kept in the table at zero rather than left out, because `MassLockKind` is
     * this table's keys: a body the renderer can point at and the rules have no
     * entry for is a compile error, and that is worth more than the line it costs.
     */
    field: 0,
    /**
     * The same slim band as a moon, which on a star is an enormous one.
     *
     * A star was the one body in the system with no grip at all — it was never
     * offered as something that could hold you — so you could sit on the surface
     * of a sun at full warp. The multiplier is kept modest precisely because the
     * radius is not: a few thousand units of radius turns a fortieth of a radius
     * into a wide shell, and anything more generous would lock a whole system.
     *
     * It also gives a jump arrival somewhere to arrive: you come out of a crossing
     * near the star, and being held on arrival is the correct reading of that.
     */
    star: 1.44,
    /**
     * Twice the band of anything else its size.
     *
     * A station's bounding radius is forty-odd units, so a proportional reach gave
     * it a grip barely twenty units above its own deck — you could be beside the
     * thing, plainly *at* it, and not be held. Every other body in the game is
     * hundreds of units across and gets hundreds of units of band from the same
     * multiplier; a station is the one case where proportional-to-size is too
     * small to be proportional-to-*purpose*.
     */
    station: 1.88,
    anomaly: 1.44,
};
/**
 * How much of the band grips completely, before it starts to fade.
 *
 * There has to be *some* of it, and the first cut had none: full grip began at
 * one radius — the surface — and faded from there out, so anywhere you could
 * actually sit beside a planet the grip was partial. A partial grip inherits a
 * fraction of the body's orbital motion, which is not a softer lock, it is a
 * permanent slow drift. You were held, the lamp was lit, and you still slid
 * away.
 *
 * A station hid it: its radius is forty-odd units and a pad sits eighteen from
 * the centre, so docking happens *inside* the old core and was pinned exactly.
 * A planet has no inside you can park in.
 *
 * So the inner half of the band is total and the outer half fades. That gives a
 * real shell of no-drift space above every surface, which is what mass lock was
 * for.
 */
const MASS_LOCK_FULL = 0.5;
/**
 * How strongly a body of this size grips at this distance.
 *
 * Proportional to the body rather than a fixed range, so a station holds you
 * within a few tens of units and a planet within a few hundred — which is what
 * makes "too close to warp" mean the same thing at every scale.
 */
export function massLockStrength(distance, bodyRadius, kind = 'moon') {
    if (bodyRadius <= 0)
        return 0;
    const reach = bodyRadius * MASS_LOCK_REACH[kind];
    const surface = bodyRadius;
    const full = surface + (reach - surface) * MASS_LOCK_FULL;
    if (distance <= full)
        return 1;
    if (distance >= reach)
        return 0;
    // Smoothstepped, so being caught fades in instead of grabbing.
    const t = (distance - full) / (reach - full);
    return 1 - t * t * (3 - 2 * t);
}
// ─── Warp ────────────────────────────────────────────────────────────────────
/** Top speed under warp. */
export const WARP_SPEED = 800;
/** How fast you must already be going to light it. */
export const WARP_ENGAGE_SPEED = 100;
/** And how slow you must be to shut it down again. */
export const WARP_DISENGAGE_SPEED = 150;
/** Warp cannot idle: below this it drops out on its own. */
export const WARP_FLOOR_SPEED = 100;
/**
 * How much of a hull's acceleration survives being in warp.
 *
 * Two numbers, not one. They used to be the same figure applied to both
 * directions, which coupled winding up to slowing down — and they are not the
 * same job: getting to speed is dead time at the start of every leg, while
 * shedding it is what decides how early you have to commit to an arrival.
 *
 * So acceleration runs a fifth above deceleration. Legs get shorter without
 * making a warp approach any less of a decision.
 */
const WARP_ACCELERATION = 0.6;
const WARP_DECELERATION = 0.5;
/**
 * Whether the warp drive may be lit right now.
 *
 * Deliberately several conditions rather than one. It is a travel mode, not a
 * boost: you have to already be under way on the main drive and clear of
 * anything with a grip on you, which makes lighting it a decision made on
 * departure rather than a button mashed in a fight.
 */
export function canEngageWarp(set, speed, massLock, landingGear = false) {
    if (set !== 'main')
        return { engaged: false, blocked: 'main drive required' };
    if (landingGear)
        return { engaged: false, blocked: 'gear down' };
    if (massLock > 0.05)
        return { engaged: false, blocked: 'mass locked' };
    // Strictly over, not merely at. The drive drops out on its own the moment
    // speed falls back to the floor, and the floor and the threshold are the same
    // number — so engaging *at* it would light the drive and shut it down on the
    // same frame.
    if (speed <= WARP_ENGAGE_SPEED) {
        return { engaged: false, blocked: `needs ${WARP_ENGAGE_SPEED} speed` };
    }
    return { engaged: true, blocked: null };
}
/** Warp can only be shut down once slow enough to survive dropping out. */
export function canDisengageWarp(speed) {
    return speed <= WARP_DISENGAGE_SPEED;
}
/**
 * Whether the drive has run out of the speed it needs to stay lit.
 *
 * This is the ordinary way out of warp, and it falls straight out of the floor:
 * the throttle cannot command less than `WARP_FLOOR_SPEED`, so easing off in
 * warp decelerates you to exactly that and no further — and arriving there ends
 * it. Throttle down, drop out. No second control, and nothing to remember.
 */
export function warpHasStalled(speed) {
    return speed <= WARP_FLOOR_SPEED;
}
/**
 * Below this, a held throttle means stop.
 *
 * The main drive holds a *setting*, and a setting is a float — so easing off
 * lands you on some fraction of a percent and the ship creeps forever. You
 * cannot see it, you cannot null it out by hand, and the only cure was
 * switching to the precision set purely to come to rest. A dead band at the
 * bottom of the throttle makes "off" reachable.
 *
 * Costs the ability to hold a deliberate crawl of under five units a second on
 * the main drive, which is the precision set's entire job anyway.
 */
export const IDLE_SNAP_SPEED = 5;
/**
 * The speed a throttle setting is actually asking for.
 *
 * Exists because of the warp floor. Clamping the commanded speed up to
 * `minSpeed` looks harmless and is not: outside warp `minSpeed` is zero, and
 * `max(0, negative)` quietly deleted reverse for every hull in the game. The
 * floor only applies where there is one.
 */
export function commandedSpeed(throttle, handling) {
    const asked = throttle * handling.maxSpeed;
    if (handling.minSpeed > 0)
        return Math.max(handling.minSpeed, asked);
    // Only where the throttle holds a setting. On the precision set the throttle
    // is a push that falls back to nothing on its own, so there is nothing to
    // snap and a dead band would just eat the last of its authority.
    if (handling.holdsThrottle && Math.abs(asked) < IDLE_SNAP_SPEED)
        return 0;
    return asked;
}
/**
 * Where the mouse is really pointing, once the ship is rolled.
 *
 * Aim is carried as a yaw and a pitch about the *world*, which is fine right up
 * until you roll: mouse-right always turned the nose about world up, so inverted
 * it steered backwards and on edge it pitched instead of turning. The pointer
 * has to be read in the frame the pilot is actually sitting in.
 *
 * So the screen-space movement is rotated by the roll before it becomes yaw and
 * pitch. At zero roll this is exactly what it always did.
 */
export function aimDelta(movementX, movementY, roll, sensitivity) {
    const cos = Math.cos(roll);
    const sin = Math.sin(roll);
    return {
        yaw: -(movementX * cos - movementY * sin) * sensitivity,
        pitch: -(movementX * sin + movementY * cos) * sensitivity,
    };
}
/**
 * What a ship can do right now.
 *
 * Depends on which thruster set is engaged, because that is the whole point of
 * having two: the precision set is slow and turns tightly, the main set is fast
 * and lumbering.
 */
export function handlingOf(performance, set, warp = false) {
    const thrust = performance.thrust[set] * performance.power.satisfaction;
    // Precision thrusters keep most of a hull's turning authority while giving up
    // nearly all its speed, so what they really buy is *agility* — turn rate per
    // unit of speed. The main set still swings the nose round faster in absolute
    // terms; the precision set turns almost as well while crawling, which is the
    // whole of what docking asks for.
    const swing = Math.sqrt(thrust) * TURN_PER_THRUST * (set === 'precision' ? 1.6 : 1);
    // Precision thrusters are speed-limited rather than thrust-limited: they are
    // for closing the last few hundred units, and a manoeuvring mode you could
    // cross a system in would just be the main drive with extra steps.
    const ceiling = warp
        ? WARP_SPEED
        : set === 'precision'
            ? CRUISE_SPEED * 0.12
            : CRUISE_SPEED;
    // Warp trades authority for reach: far more speed, and much less power to
    // change it. Committing to one is committing to the distance.
    const push = warp ? WARP_ACCELERATION : 1;
    const brake = warp ? WARP_DECELERATION : 1;
    return {
        maxSpeed: thrust > 0 ? ceiling : 0,
        minSpeed: warp ? WARP_FLOOR_SPEED : 0,
        // The main drive holds a cruise; the precision set does not. Manoeuvring
        // thrusters are something you hold down to move and let go to stop, which
        // is what makes them safe to dock on — a throttle that kept its setting
        // would drift you into the pad the moment your attention moved.
        holdsThrottle: set === 'main',
        acceleration: thrust * ACCELERATION_PER_THRUST * push,
        deceleration: thrust * ACCELERATION_PER_THRUST * brake,
        turnRate: swing,
        rollRate: swing * ROLL_FRACTION,
        strafeAcceleration: thrust *
            ACCELERATION_PER_THRUST *
            STRAFE_FRACTION *
            push *
            (set === 'precision' ? PRECISION_STRAFE_BONUS : 1),
    };
}
/**
 * How much sideways authority remains at a given speed, 0..1.
 *
 * Full while manoeuvring, gone at speed. Smoothstepped rather than clamped, so
 * it fades out instead of switching off at a threshold nobody was told about.
 */
/**
 * How much of a hull's turn rate survives at this speed.
 *
 * Logarithmic, and steeply so. Turn rate used to be a constant, which meant a
 * ship crossing a system at cruise could swap ends as readily as one nudging
 * onto a pad — so speed cost you nothing and there was never a reason to slow
 * down for anything.
 *
 * A log curve is the right shape because it is generous where the decisions are
 * small and unforgiving where they are large: manoeuvring speeds keep most of
 * their authority, cruise keeps about a quarter, and warp keeps a seventh. At
 * warp that is a half-turn in something over a minute, which is the point —
 * warp is for going somewhere, and pointing somewhere else means slowing down
 * first.
 *
 * Never reaches zero. A ship that cannot turn at all is a ship that cannot
 * recover from a mistake, and every log curve keeps giving you a little.
 */
export function turnAuthority(speed) {
    return 1 / (1 + TURN_FALLOFF * Math.log1p(Math.abs(speed) / TURN_REFERENCE));
}
export function strafeAuthority(speed, maxSpeed) {
    if (maxSpeed <= 0)
        return 0;
    const fraction = Math.abs(speed) / maxSpeed;
    if (fraction <= STRAFE_FALLOFF_START)
        return 1;
    if (fraction >= 1)
        return 0;
    const t = (fraction - STRAFE_FALLOFF_START) / (1 - STRAFE_FALLOFF_START);
    return 1 - t * t * (3 - 2 * t);
}
/**
 * Swing a heading toward a target, easing in and out.
 *
 * ── Superseded by `stepOrientation` ─────────────────────────────────────────
 *
 * The cockpit no longer calls this: the hull carries a rotation now, and eases
 * it with `stepOrientation`, which does the same thing without a pole straight
 * up. Kept because it is still the clearest statement of the easing rule and
 * because its tests pin the behaviour the rotation version was built to match —
 * if the two ever disagree, this is what says which one changed.
 *
 * The previous version stepped at a flat maximum rate, which starts and stops
 * instantly — the hull snapped into motion the moment the reticle moved and
 * stopped dead on arrival. Carrying an angular *rate* and limiting how fast
 * that rate may change gives both ends for free: the ship winds up into a turn
 * and settles out of it.
 *
 * The wanted rate is proportional to how far there is to go, which is what
 * produces the ease-out — the error shrinks, so the demand shrinks with it —
 * and the acceleration limit produces the ease-in.
 */
export function stepTurn(state, target, handling, seconds) {
    const error = wrapAngle(target - state.angle);
    // How quickly the rate itself may change. Tied to the turn rate so a nimble
    // hull is crisp and a heavy one wallows, rather than every ship sharing one
    // feel with different top speeds.
    const agility = handling.turnRate * TURN_RESPONSE;
    const wanted = Math.max(-handling.turnRate, Math.min(handling.turnRate, error * TURN_GAIN));
    const change = Math.max(-agility * seconds, Math.min(agility * seconds, wanted - state.rate));
    const rate = state.rate + change;
    const angle = state.angle + rate * seconds;
    // Settle rather than hunting: once inside a hair of the target with almost no
    // rate left, stop, or floating point keeps the ship imperceptibly rocking.
    if (Math.abs(wrapAngle(target - angle)) < 1e-4 && Math.abs(rate) < 1e-3) {
        return { angle: target, rate: 0 };
    }
    return { angle, rate };
}
/**
 * How aggressively the wanted rate scales with the angle left to cover.
 *
 * Lower is heavier: the ship commits to a slower turn and holds it, instead of
 * spinning hard and stopping the instant the reticle is met.
 */
const TURN_GAIN = 1.2;
/**
 * How fast the turn rate itself may change, as a multiple of the turn rate.
 *
 * This is the whole feeling of weight. High, and the ship reaches full rate
 * within a frame and reads as weightless whatever its top rate; low, and it
 * visibly leans into the turn and takes a moment to stop.
 */
const TURN_RESPONSE = 0.9;
/**
 * Turn a heading toward a target by at most `maxStep`.
 *
 * Angles wrap, so the naive difference sends a ship the long way round whenever
 * a turn crosses the seam — which looks like the hull suddenly deciding to
 * rotate 350° rather than 10°.
 */
export function approachAngle(current, target, maxStep) {
    const difference = wrapAngle(target - current);
    if (Math.abs(difference) <= maxStep)
        return target;
    return current + Math.sign(difference) * maxStep;
}
/** Fold an angle into −π..π. */
export function wrapAngle(angle) {
    const wrapped = ((angle + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    return wrapped - Math.PI;
}
/**
 * Heat.
 *
 * ── Thrusters cannot kill you ───────────────────────────────────────────────
 *
 * Each set of thrusters drives the hull toward a ceiling and no further: a
 * quarter of the gauge on the main set, a tenth on precision. Flying is not
 * supposed to be a resource you run out of — you can burn as long as you like
 * and the needle settles where it settles.
 *
 * The ceiling is per *tier*, not per thruster. Bolting on four mains does not
 * heat a ship four times as fast, because otherwise the fastest hull in the game
 * would also be the one that could not use its own engines, and every design
 * would converge on the fewest thrusters that still moved.
 *
 * ── Suns can ───────────────────────────────────────────────────────────────
 *
 * What is dangerous is what you fly near. A star radiates across a band above
 * its surface, and at the surface it drives the gauge to the top on its own —
 * so going into one is fatal regardless of how gently you do it, which is the
 * point. Volcanic worlds do the same over a much shorter distance.
 *
 * The two add. Burning hard past a star is worse than drifting past one, which
 * is the decision the mechanic exists to create.
 */
/** The most heat each thruster set puts into a hull at full throttle, 0..1. */
export const THRUSTER_HEAT = { main: 0.25, precision: 0.1 };
/**
 * How far a hot body radiates, as a multiple of its own radius.
 *
 * A star's band is wide in absolute terms because a star is — a couple of
 * thousand units of dangerous space around something five thousand across. A
 * volcanic world gets a narrow one: close enough that you have to mean it.
 */
export const HEAT_REACH = { star: 1.6, volcanic: 1.25 };
/** Heat at which the hull starts cooking, and where the gauge tops out. */
export const HEAT_SCORCHING = 0.75;
export const HEAT_FATAL = 1;
/**
 * Hull points a second, at the bottom and the top of the scorching band.
 *
 * Quartered from where these started, which was punishing rather than tense. At
 * the old rate the top of the gauge took a starter hull apart in a couple of
 * seconds — long enough to notice the warning and not long enough to act on it,
 * so a moment's inattention near a star was simply a lost ship. Getting too
 * close should be a scare you fly out of, and only an outright refusal to leave
 * should cost the hull.
 *
 * The shape is unchanged and so is everything derived from it: `damageHull`
 * still divides by a line's strength, so plating still buys proportionally more
 * time in a corona, and the bottom of the band is still a slow cook rather than
 * a cliff.
 */
export const HEAT_DAMAGE_MIN = 0.25;
export const HEAT_DAMAGE_MAX = 3.75;
/**
 * What running hot costs the hull, in points a second.
 *
 * Heat does not kill you. It cooks the ship, and the ship is what dies — which
 * is both a better read on screen and the only version that lets plating mean
 * anything: this is *raw* damage, and `damageHull` divides it by a line's
 * strength, so a sintered hull sits in a corona a great deal longer than a bare
 * one. A flat death at the top of the gauge made the best plating in the game
 * worth exactly nothing against the one hazard that could not be avoided by
 * flying well.
 *
 * Nothing below three-quarters. The gauge has to be able to run warm — a main
 * burn settles at a quarter and precision at a tenth — without the hull quietly
 * dissolving, or thrusters would be a resource after all.
 *
 * Every line, not the ones facing the star. A hull sitting in radiant heat is
 * not being hit on one side; there is nowhere on the ship that is not in it.
 */
export function heatDamage(heat) {
    if (heat <= HEAT_SCORCHING)
        return 0;
    const into = Math.min(1, (heat - HEAT_SCORCHING) / (HEAT_FATAL - HEAT_SCORCHING));
    return HEAT_DAMAGE_MIN + (HEAT_DAMAGE_MAX - HEAT_DAMAGE_MIN) * into;
}
/**
 * Where the gauge is pinned to the top, as a multiple of the body's radius.
 *
 * Above the surface, and that margin is the whole reason contact is fatal. Heat
 * *approaches* its target rather than marching to it, so a target of exactly one
 * is one the gauge closes on forever and never reaches. Put the top at the
 * surface and a hull resting against a star settles at a hair under fatal and
 * sits there — and it will be resting against it, because a collision stops the
 * ship a hull's width off rather than at the surface exactly.
 *
 * So everything from a little way out is already past the top, and the overshoot
 * grows as you go in. Flying into a star is then fatal at any speed, which is
 * the point of the mechanic.
 */
const SCORCH = 1.05;
/**
 * How hard a body cooks a hull at this distance.
 *
 * Zero at the edge of the band, one just above the surface, and beyond one from
 * there inward — see `SCORCH`.
 */
export function radiantHeat(distance, bodyRadius, kind) {
    if (bodyRadius <= 0)
        return 0;
    const reach = bodyRadius * HEAT_REACH[kind];
    const skin = bodyRadius * SCORCH;
    if (distance >= reach)
        return 0;
    if (distance <= skin)
        return 1 + (skin - distance) / bodyRadius;
    return (reach - distance) / (reach - skin);
}
/**
 * Where the gauge is heading, given what the ship is doing and where it is.
 *
 * Not clamped to the top: see `radiantHeat`. The gauge itself is clamped, and it
 * is the *target* that has to be able to exceed the top for the top to be
 * reachable at all.
 */
export function heatTarget(options) {
    const burn = THRUSTER_HEAT[options.set] * Math.min(1, Math.abs(options.throttle));
    return burn + Math.max(0, options.radiant);
}
/**
 * How fast the gauge closes on its target, per second.
 *
 * Proportional to the gap, so the same rule gives a slow drift up to a
 * thruster's quarter and an urgent climb when a star has set the target above
 * the top of the gauge. Shedding is quicker than gaining: cooling down should
 * not be a thing you wait out.
 */
const HEAT_GAIN = 0.32;
const HEAT_SHED = 0.55;
/** Move the gauge toward its target. */
export function stepHeat(heat, target, seconds) {
    const rate = target > heat ? HEAT_GAIN : HEAT_SHED;
    const closed = Math.min(1, rate * seconds);
    return Math.max(0, Math.min(1, heat + (target - heat) * closed));
}
//# sourceMappingURL=flight.js.map